import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { kc } from '../auth/keycloak';
import { getCurrentUser } from '../auth/useUser';
import { useIdleTimer } from '../state/useIdleTimer';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { Settings } from '../pages/Settings';
import { Audit } from '../pages/Audit';
import { Cases } from '../pages/Cases';
import { Notifications } from '../pages/Notifications';
import { Dashboard } from '../pages/Dashboard';
import { ReVets } from '../pages/ReVets';
import { Settlement } from '../pages/Settlement';
import { Changes } from '../pages/Changes';
import { Legal } from '../pages/Legal';
import { CureAppeals } from '../pages/CureAppeals';
import { Onboarding } from '../pages/Onboarding';
import { Intake } from '../pages/Intake';
import { IpDisputes } from '../pages/IpDisputes';
import { CaseDetail } from '../pages/CaseDetail';
import { UserManagement } from '../pages/UserManagement';
import { Customers } from '../pages/Customers';
import { useEffect, useState } from 'react';
import { Help } from '../pages/Help';
import { Modals } from '../components/modals';
import { ReportBugModal } from '../components/modals/ReportBugModal';
import { ProfileMenu } from '../components/ProfileMenu';
import { Profile } from '../pages/Profile';
import { Preferences } from '../pages/Preferences';
import { LegalAcceptanceGate } from '../components/LegalAcceptanceGate';
import { fetchDeployedVersion } from '../api/version';

// The 382 brand mark: the gold "382" numerals, cut from
// deploy/keycloak/themes/bcid/login/resources/img/382web.png (source crop
// x1..296 / y8..126) and scaled to 200x80 — a 4-bit indexed PNG, ~1.5 KB.
//
// Only the numerals, not the full lockup. The master is a 296x160 raster whose
// "communications" wordmark is 17px tall at source scale, so at any header size
// it renders as a smear; the text beside the mark carries that word instead.
//
// The master's black plate is NOT baked in. Every pixel of the source is one
// flat gold over pure black, so the plate was recovered as straight alpha and
// the black is applied in CSS only where it is needed (gold on the portal's
// near-white header). On the staff sidebar the mark drops straight onto the
// dark background with no rectangle showing through.
//
// Inlined as a data: URI rather than imported as an asset: the CSP is
// `img-src 'self' data:` with no external origins, and a data: URI needs
// nothing in the docroot and no base-path juggling (the portal is served from
// /apply/). Duplicated byte-for-byte between apps/spa and apps/portal for the
// same reason lib/theme.ts is — the customer bundle must not reach into
// apps/spa (see apps/portal/vite.config.ts).
const BRAND_MARK_382 =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAABQBAMAAACuQyeDAAAAMFBMVEX9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgr9xgrvY+34AAAAD3RSTlMAESIzRFVmd4iZqrvM3e5GKvWZAAAFMUlEQVR42u1YTWxbRRD2e06ckJbErugBkFo7P6iUSjhNo0Ja0QhxJkYqLVyqioJKJFACiButKoQQB5Sk5UcCBYIIB4qA5AZC0KYqFATF9hEQWA96AqW2gWJiY7/F9tvZt//7Kju3zMX27nq/3Zmd+WYmFNqQDVkvsQKu67diu4cdZihxOVNwnTaeZXC+gCRSfS/eNojwc0gl1RfahGHPII280h6QnToM5B5ty0XOa0HQWjtAbkYGacdVpkwgv7VBWxkTSBv01WnCQG7SdE4jSIc5arQO0mm+bLJldW2j9IIK1HdfnJZvkoAvtUdtK2aB2B2TZMmWlm8yAec9xc/sh5m/WgaZxTuVRS2A8v5uWV0QzP8TZtx3ApKSGSQLW4pTK/gTtQwCkheHikGDhnFFFH+2QLRmEIezzbqAZM3OgIJGptGDCepf33+W5W9iqR9ewT/zsJ/SbM+kuQu9zGUjq0/D1GY8UhJBFvAUPGX74XTezYO4+W+PMTq+Xwzg4OBdyoAehqXTyoRjkVq+Q5bs4FOEgbS+4kw/cJJdaR2R7PKur+mllMRci0cw/c7hge++yBX9zLN/L/yp3N386P5XskltEGzUrWXVW6gxonDahGe4SCrPMuTTCBvBXtKzb9nTli2fvQJ+ktKGRveE3gvOOjqavol/JZyQlzmru8g1vKpHkSxj/LC36uuT2KliQy9GaV9zn42nlPeo7IfEH39ePYd/D3s3wMO7BGc70BxZ8T1iQXWPUpw1bPle8qe7vccx7v26h/5BPTcfZCZAUj8lcPQU/bwmBHYNMyBhrU2+jFI0zTBbH4CQKFxTp1N3TOse1z7PXnZU4OgK7NIw/I8f1B25rAzyHRf0T/iNFYeQG5LSZwPkh8NC6KOD/FhUDxKZniZcXJMyq5y0dlAg4QsmWnu8GcyO0g+WOmpR4af2U5S6/Fz4j/nzRbhVMTZwHGwWSdZXosk4bxObyREITT72YF0OHX/bIxDPBe6EZ3QpwYV64jxzJJ5HxbSQr8N6PVpj4wW834qQSYQhFP8p1eLtJMwyzN1HJ1K1g8vNO1/Fp7uyTSz0MNP8s9mvVeJWyIrG+u3E0APy3KCP8rHKMaxCGBgXz9oLwR60ZA++9M18usYQQUgDsoY3jQDFS/KuCMfUQzPqIC0FQWveP2+Am0m8JczSW0TWfzmjBcHTPcrKgQIZVzJoNa4H8Th+E9zECLJLFj4vGtLUrvHrapZZz8iSlSdFT9/S35A9k1S9uSmA4Zs36bzOjog15peCPVzuQksPDSLh+OrzmlYAuiTJ+E8fENaN0tXQHmaHvJPLr36Y1fYbllMAQhjirihfWNmQWqLGTAxn+J+jQiFXyBVzxqJkgTyoiM+zPMYTzDv1YtxiXFGfbE3VT9L7lrxA8mlo36sz/ulsd2QvySGqDtBv7YSjeIUTy0xm3JBMEhJpEiAbakkTFHs7ZYNrN9YHPkr5ubeq58A+USoTmzW2otCvpC1WUtaMzcTRukiNjtHmMReeC4bCtWGT1ea3W19bgsGtr1NZ0k9GDLTsn/k+YfZnh3VcToos/ymlxDRaeJnz1FVTnNDL6mvGZumbQep4lNWBhD4xbFGZCwLinpJOlTB2JWXIIIM1RLqkyjxNHoe2aVuKhoLYJFSWZdSVWeLQo1mNsnYG7RV1iMzJKOm2j1Ut4feTfBtPchOoxfi2x++PsET7UFq2wVm/r6EqPBsgFvGi3XG/E+D+co6P1AMjsRG2r9aXyV/2bW4fUnSHP10JbciGrJ/8D8UNOerp42RcAAAAAElFTkSuQmCC';

// #398 — the four public legal documents. They are flat HTML served by nginx
// from /legal/, outside both SPA bundles: this app is `onLoad: 'login-required'`
// so it cannot host a public page, and the Keycloak login screen has to link to
// the same copies before anyone has signed in. Hence absolute paths and plain
// anchors — these leave the SPA, they are not `setPage` routes.
//
// Duplicated in apps/portal/src/app/App.tsx. The two bundles deliberately share
// no code (apps/portal/vite.config.ts), and four href/label pairs are cheaper to
// keep in step than a shared package.
const LEGAL_LINKS: Array<[string, string]> = [
  ['/legal/terms.html', 'Terms of Service'],
  ['/legal/privacy.html', 'Privacy Policy'],
  ['/legal/acceptable-use.html', 'Acceptable Use Policy'],
  ['/legal/cookies.html', 'Cookie Notice'],
];

export function App() {
  const user = getCurrentUser()!;
  const { stats, toast, dismissToast, casesLoading, casesError } = useAppData();
  const { page, setPage } = useUIState();
  // #152 follow-up: the bug reporter is reachable from every page (sidebar,
  // above the user block), so the auto-attached page context reflects where
  // the user actually was — not just "help".
  const [reportingBug, setReportingBug] = useState(false);
  // #817 — the running release for the footer, so staff can say which version
  // they are on. Fetched once per page load; null (the fetch failed or the box
  // has no /version.json) renders nothing rather than an error.
  const [deployedVersion, setDeployedVersion] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void fetchDeployedVersion().then(v => { if (live) setDeployedVersion(v); });
    return () => { live = false; };
  }, []);

  useIdleTimer(15 * 60 * 1000, () => kc.logout());

  const navSections = [
    { label: null as string | null, items: [
      { id: 'dashboard', label: 'Dashboard', icon: '◈', badge: 0 },
    ]},
    { label: 'ONBOARDING AGENT', items: [
      // #472 — one nav entry covers all three lists on the Intake Queue page,
      // so the honest number is their sum: items waiting on that page.
      { id: 'intake',      label: 'Intake Queue',         icon: '⇲', badge: stats.intakePending + stats.tnChangeRequestsPending + stats.companyChangesPending },
      { id: 'onboarding',  label: 'Applications',         icon: '◫', badge: stats.onboardingSubmitted },
      { id: 'ip-disputes', label: 'IP & SHAFT Disputes',  icon: '©', badge: stats.activeIpDisputes + stats.activeShaftDisputes },
      { id: 'changes',     label: 'Change Tracking',      icon: 'Δ', badge: 0 },
    ]},
    { label: 'VETTING AGENT', items: [
      { id: 'cases',         label: 'Vetting Cases',         icon: '◰', badge: 0 },
      { id: 'notifications', label: 'Notifications',         icon: '◉', badge: stats.pendingNotification },
      { id: 'cure-appeals',  label: 'Cure & Appeals',        icon: '⚖', badge: stats.cureWindowOpen + stats.appealsActive },
      { id: 'revets',        label: 'Re-Vet Schedule',       icon: '↻', badge: stats.revetsDue },
      { id: 'settlement',    label: 'Settlement Platform',   icon: '⬡', badge: stats.pendingSync },
    ]},
    { label: 'ADMINISTRATION', items: [
      { id: 'legal',    label: 'Legal & Compliance', icon: '§', badge: 0 },
      { id: 'audit',    label: 'Audit Trail',        icon: '◔', badge: 0 },
      { id: 'settings', label: 'Settings',           icon: '⚙', badge: 0 },
      // #150/#151 — in-portal Help: bundled user guide (all roles) plus
      // admin-gated runbooks. The Help page itself scopes runbook content
      // to canAdmin; the nav entry is visible to everyone.
      { id: 'help',     label: 'Help',               icon: '?', badge: 0 },
      // #139 — first role-filtered nav item (admin only). The API 403s
      // non-admins regardless; this filter just keeps the entry out of the
      // sidebar for everyone else.
      { id: 'user-management', label: 'User Management', icon: '⛊', badge: 0, adminOnly: true },
      // #141/#323 — Customers: the companies whose applications this portal
      // accepts, and the credentials each owns (admin only). Replaced the old
      // credential-first "Customer API" entry when the customers table became
      // the tenant authority. The API 403s non-admins regardless.
      { id: 'customers', label: 'Customers', icon: '⇄', badge: 0, adminOnly: true },
    ]},
  ].map(section => ({
    ...section,
    items: section.items.filter(n => !('adminOnly' in n && n.adminOnly) || user.canAdmin),
  }));

  const pageMap: Record<string, () => JSX.Element> = {
    dashboard: () => <Dashboard />,
    intake: () => <Intake />,
    onboarding: () => <Onboarding />,
    cases: () => <Cases />,
    notifications: () => <Notifications />,
    'cure-appeals': () => <CureAppeals />,
    revets: () => <ReVets />,
    settlement: () => <Settlement />,
    changes: () => <Changes />,
    legal: () => <Legal />,
    'ip-disputes': () => <IpDisputes />,
    audit: () => <Audit />,
    settings: () => <Settings />,
    'user-management': () => <UserManagement />,
    customers: () => <Customers />,
    help: () => <Help />,
    'case-detail': () => <CaseDetail />,
    // #399 — reached from the account menu in the sidebar's user block, not
    // from the nav: these are about the person, not about the work.
    profile: () => <Profile />,
    preferences: () => <Preferences />,
  };

  // #401 — everything this component renders, including the two splash screens
  // below, goes inside LegalAcceptanceGate. Built as one expression rather than
  // returned early from three places so the gate wraps ALL of them: "accept
  // before reaching any page" has to mean the loading and error screens too,
  // and three separate returns are three chances for the next one to be added
  // outside the wrapper.
  const shell = (() => {
    if (casesLoading) {
      return (
        <div style={{ ...styles.app, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Loading cases…</div>
        </div>
      );
    }
    if (casesError) {
      return (
        <div style={{ ...styles.app, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 24 }}>
          <div style={{ background: C.dangerBg, color: C.danger, padding: 16, borderRadius: 8, maxWidth: 480 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Failed to load cases</div>
            <div style={{ fontSize: 12, fontFamily: 'monospace' }}>{casesError}</div>
          </div>
        </div>
      );
    }

    return (
    <div style={{ ...styles.app, display: 'flex' }}>
      {toast && (() => {
        // Success is a confirmation, error is a failure report: they get
        // different color, and different ARIA. `alert` is assertive and
        // interrupts a screen reader mid-sentence, which is right for a
        // failure and wrong for "Company name updated."
        const ok = toast.variant === 'success';
        const fg = ok ? C.success : C.danger;
        return (
          <div
            style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, background: ok ? C.successLight : C.dangerLight, color: fg, padding: '12px 16px', borderRadius: 8, maxWidth: 480, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 12 }}
            role={ok ? 'status' : 'alert'}
            data-testid={`toast-${toast.variant}`}
          >
            <span>{toast.message}</span>
            <button type="button" aria-label="Dismiss notification" style={{ ...styles.btnUnstyled, color: fg, fontWeight: 700, fontSize: 15, lineHeight: 1 }} onClick={dismissToast}>×</button>
          </div>
        );
      })()}
      <div style={styles.sidebar}>
        <div style={{ padding: '20px 20px 12px' }}>
          {/* #359 — the mark replaces the gold "382" text that stood here, so
              staff and customers see the same brand. No plate here: the mark
              carries no background of its own, and gold already reads against
              the dark sidebar. */}
          <img
            src={BRAND_MARK_382}
            alt="382 Communications"
            width={90}
            height={36}
            style={{ display: 'block', marginBottom: 2 }}
          />
          <div style={{ fontSize: 10, color: C.sideText, letterSpacing: 1.5, textTransform: 'uppercase' }}>BCID Portal</div>
        </div>
        <nav aria-label="Primary" style={{ flex: 1, paddingTop: 4, overflowY: 'auto' }}>
          {navSections.map((section, si) => (
            <div key={si}>
              {section.label && (
                <div style={{ padding: '14px 20px 6px', fontSize: 9, fontWeight: 700, color: C.sideTextDim, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                  {section.label}
                </div>
              )}
              {section.items.map(n => (
                <button key={n.id} type="button" aria-current={page === n.id ? 'page' : undefined} style={{ ...styles.btnUnstyled, display: 'block', width: '100%', ...styles.sideLink, ...(page === n.id ? styles.sideLinkActive : {}) }} onClick={() => setPage(n.id)}>
                  <span style={{ marginRight: 8 }}>{n.icon}</span> {n.label}
                  {n.badge > 0 && <span style={{ ...styles.badge, background: 'rgba(239,68,68,0.2)', color: '#ef4444', marginLeft: 8, fontSize: 10 }}>{n.badge}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div style={{ padding: 16, borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }}>
          <button style={{ ...styles.btn, ...styles.btnSmall, marginBottom: 10, width: '100%', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: C.sideText }} onClick={() => setReportingBug(true)}>Report a bug</button>
          {/* #399 — the name/role block became the account menu. Sign Out
              moved inside it, alongside the profile, settings, and the two
              Keycloak self-service actions; it is still one click from here. */}
          <ProfileMenu user={user} onNavigate={setPage} />
        </div>
      </div>

      <div style={styles.main}>
        {(pageMap[page] || pageMap.dashboard)()}

        {/* Staff read these too — an analyst quoting the acceptable-use rules
            to a registrant needs the same canonical copy the customer sees. */}
        <footer style={styles.legalFooter}>
          {LEGAL_LINKS.map(([href, label]) => (
            <a key={href} href={href} style={styles.legalLink}>{label}</a>
          ))}
          <span style={{ fontSize: 11, color: C.textMuted }}>
            382 Communications — Branded Calling ID
          </span>
          {deployedVersion && (
            <span data-testid="deployed-version" style={{ fontSize: 11, color: C.textMuted }}>
              Version {deployedVersion}
            </span>
          )}
        </footer>
      </div>

      {reportingBug && <ReportBugModal page={page} onClose={() => setReportingBug(false)} />}
      <Modals />
    </div>
    );
  })();

  return <LegalAcceptanceGate>{shell}</LegalAcceptanceGate>;
}
